import CookieIcon from '@mui/icons-material/Cookie';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Fade from '@mui/material/Fade';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Paper from '@mui/material/Paper';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import cookies from '../content/cookies';
import { useCookies } from '../hooks/CookieProvider';

const StyledContent = styled(DialogContent)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  background: theme.palette.background.gradient,
  color: theme.palette.text.primary,
  h2: {
    margin: '1rem 0',
  },
  '.MuiButtonBase-root': {
    color: theme.palette.text.primary,
  },
  '.MuiFormControlLabel-label.Mui-disabled': {
    color: theme.palette.text.primary,
  },
  '.Mui-disabled': {
    '.MuiButtonBase-root': {
      color: '#999 !important',
    },
  },
  [theme.breakpoints.up('md')]: {
    position: 'fixed',
    display: 'flex',
    alignItems: 'top',
    justifyContent: 'space-evenly',
    top: 'auto',
    bottom: 0,
    flexDirection: 'row',
    padding: '2rem',
    h2: {
      margin: 0,
    },
    '.infoBox': {
      maxWidth: '80%',
    },
  },
}));

const StyledButtonContainer = styled(Box)({
  maxWidth: '600px',
  borderTop: '1px solid rgba(0, 0, 0, 0.5)',
  marginTop: '1.5rem',
  paddingTop: '0.5rem',
  button: {
    margin: '0 0.5rem 0 0',
    padding: '0 0.5rem',
    textTransform: 'none',
    borderLeft: '1px solid rgba(0, 0, 0, 0.5)',
    borderRight: '1px solid rgba(0, 0, 0, 0.5)',
  },
  'button:hover, button:focus-visible': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: '1rem',
  background: theme.palette.background.grayGradient,
  '.MuiTableCell-root': {
    color: theme.palette.text.primary,
  },
  '.MuiTableBody-root': {
    '.MuiTableRow-root': {
      '&:nth-of-type(odd)': {
        background: 'rgba(0, 0, 0, 0.1)',
      },
      '&:hover, &:focus-visible': {
        background: 'rgba(0, 0, 0, 0.2)',
      },
    },
  },
}));

const ActionButtons = ({ setShowCookieDetails }) => {
  const cookieConsent = useCookies();
  const { acceptAllCookies, declineCookies, saveConsent } = cookieConsent;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Button
        fullWidth
        size='large'
        variant="contained"
        onClick={() =>  { setShowCookieDetails(false); acceptAllCookies(); }}
        sx={{ mb: '0.5rem', backgroundColor: 'rgba(255, 255, 255, 0.4)' }}
      >
        Alle akzeptieren
      </Button>
      <Button
        fullWidth
        size='large'
        variant="contained"
        onClick={() =>  { setShowCookieDetails(false); declineCookies(); }}
        sx={{ mb: '0.5rem', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
      >
        Nur erforderliche
      </Button>
      <Button
        fullWidth
        size="large"
        variant="contained"
        onClick={() => { setShowCookieDetails(false); saveConsent(); }}
        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
      >
        Speichern
      </Button>
    </Box>
  );
};

ActionButtons.propTypes = {
  setShowCookieDetails: PropTypes.func.isRequired,
};

const CookieTable = ({ cookies }) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const fixed150 = { width: !mobile ? 150 : 'auto' };
  const fixed250 = { width: !mobile ? 250 : 'auto' };
  return (
    <StyledTableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="cookie table">
        <TableHead>
          <TableRow>
            <TableCell sx={fixed150}>Name</TableCell>
            <TableCell sx={fixed250}>Anbieter</TableCell>
            <TableCell>Zweck</TableCell>
            <TableCell sx={fixed250}>Gültigkeit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cookies.length && cookies.map((cookie) => (
            <TableRow
              key={cookie.name}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">{cookie.name}</TableCell>
              <TableCell>{cookie.provider}</TableCell>
              <TableCell>{cookie.purpose}</TableCell>
              <TableCell>{cookie.validity}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
    );
}

CookieTable.propTypes = {
  cookies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const findCookie = (cookieKey) => {
  const cookie = cookies.find((c) => c.key === cookieKey);
  return cookie || cookieKey;
}

export const isLegalInfo = (location) => {
  let check = false;
  ['impressum', 'datenschutz'].forEach((path) => {
    if (location.pathname.includes(path)) {
      check = true;
    }
  });
  return check;
};

const CookieConsent = () => {
  const cookieConsent = useCookies();
  const location = useLocation();
  const navigate = useNavigate();
  const [showCookieDetails, setShowCookieDetails] = React.useState(false);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAccepted, saveConsent, showCookieConsent, toggleCookieGroup } = cookieConsent;

  return showCookieConsent && !isLegalInfo(location) && (
    <Dialog
      open={showCookieConsent}
      onClose={saveConsent}
      fullScreen={mobile}
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
    >
      <Fade in={showCookieConsent}>
        <StyledContent>
          <Box className="infoBox">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', mb: '1rem' }}>
              <CookieIcon sx={{ fontSize: 42 }} />
              <Typography id="cookie-consent-title" variant="h5">
                Cookie-Einstellungen
              </Typography>
            </Box>
            <Typography id="cookie-consent-description">
              Diese Website verwendet Cookies und vergleichbare Technologien zur Bereitstellung bestimmter Funktionen, 
              zur Einbindung externer Inhalte und Dienste sowie zur anonymen statistischen Auswertung der Nutzung. 
              Dabei können je nach gewählter Funktion Daten auch an Drittanbieter übermittelt werden. Ihre Einwilligung 
              ist freiwillig und kann jederzeit über den Link „Cookies“ am Seitenende angepasst oder widerrufen werden.
            </Typography>
            <FormGroup
              sx={{
                margin: '1rem 0',
                display: 'flex',
                flexDirection: showCookieDetails ? 'column' : 'row',
              }}
            >
              <FormControlLabel disabled control={<Checkbox defaultChecked />} label="Erforderliche Cookies" />
              {showCookieDetails && <CookieTable cookies={[
                findCookie('hideCookieConsent'),
                findCookie('acceptedCookieGroups'),
              ]} />}
              <FormControlLabel control={<Checkbox onChange={() => { toggleCookieGroup('functional'); }} checked={isAccepted('functional')} />} label="Funktionale Cookies" />
              {showCookieDetails && <CookieTable cookies={[
                findCookie('turnstile'),
              ]} />}
              <FormControlLabel control={<Checkbox onChange={() => { toggleCookieGroup('statistics'); }} checked={isAccepted('statistics')} />} label="Anonyme Statistik" />
              {showCookieDetails && <CookieTable cookies={[
                findCookie('matomo'),
              ]} />}
            </FormGroup>
            {mobile && <ActionButtons setShowCookieDetails={setShowCookieDetails} />}
            <StyledButtonContainer>
              <Button
                variant="text"
                onClick={() => { setShowCookieDetails(!showCookieDetails); }}
              >
                {showCookieDetails ? 'Weniger Informationen' : 'Mehr Informationen'}
              </Button>
              <Button
                variant="text"
                onClick={() => { navigate("/impressum"); }}
              >
                Impressum
              </Button>
              <Button
                variant="text"
                onClick={() => { navigate("/datenschutz"); }}
              >
                Datenschutzhinweise
              </Button>
            </StyledButtonContainer>
          </Box>
          {!mobile && <ActionButtons setShowCookieDetails={setShowCookieDetails} />}
        </StyledContent>
      </Fade>
    </Dialog>
  );
}

export default CookieConsent;
